import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add,
  CalendarMonth,
  Description,
  EventNote,
  History,
  Person,
  PersonAdd,
  Search,
  Vaccines,
  CurrencyRupee,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@context/AuthContext';
import { patientService } from '@services/patientService';
import { useDebounce } from '@hooks/useDebounce';
import { getRecentPatients } from '../../utils/recentPatients';
import type { Patient } from '../../types';

interface GlobalSearchProps {
  open: boolean;
  onClose: () => void;
}

interface PaletteItem {
  key: string;
  group: 'Actions' | 'Recent patients' | 'Patients';
  label: string;
  hint?: string;
  icon: React.ReactNode;
  patientId?: string;
  run: () => void;
}

const STAFF_ROLES = ['doctor', 'admin', 'receptionist'];

/**
 * Ctrl/⌘+K command palette: jump to any patient (server search), recent patients,
 * and the common actions for the signed-in role — fully keyboard driven.
 */
const GlobalSearch: React.FC<GlobalSearchProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = (user?.role as string | undefined)?.toLowerCase() || '';
  const isDoctor = role === 'doctor';
  const isStaff = STAFF_ROLES.includes(role);

  const [term, setTerm] = useState('');
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);
  const debounced = useDebounce(term.trim(), 250);

  useEffect(() => {
    if (open) {
      setTerm('');
      setActive(0);
    }
  }, [open]);

  const { data: patients = [], isFetching } = useQuery({
    queryKey: ['patients', 'palette', debounced],
    queryFn: () => patientService.search(debounced),
    enabled: open && isStaff && debounced.length >= 2,
    staleTime: 30 * 1000,
  });

  const go = (path: string) => {
    onClose();
    navigate(path);
  };

  const items: PaletteItem[] = useMemo(() => {
    const q = term.trim().toLowerCase();
    const matches = (text: string) => !q || text.toLowerCase().includes(q);

    type Action = Omit<PaletteItem, 'group'>;
    const candidates: (Action | false)[] = [
      isDoctor && { key: 'new-rx', label: 'New prescription', hint: 'Shift+N', icon: <Description />, run: () => go('/prescriptions/create') },
      isStaff && { key: 'new-appt', label: 'Book appointment', icon: <Add />, run: () => go('/appointments/create') },
      isStaff && { key: 'new-patient', label: 'Register patient', icon: <PersonAdd />, run: () => go('/patients?new=1') },
      isStaff && { key: 'appts', label: "Today's appointments", icon: <EventNote />, run: () => go('/appointments') },
      isStaff && { key: 'calendar', label: 'Appointment calendar', icon: <CalendarMonth />, run: () => go('/appointments/calendar') },
      isStaff && { key: 'vacc', label: 'Vaccinations due', icon: <Vaccines />, run: () => go('/vaccinations') },
      isStaff && { key: 'billing', label: "Billing · today's collection", icon: <CurrencyRupee />, run: () => go('/billing') },
    ];
    const actions: PaletteItem[] = candidates
      .filter((a): a is Action => !!a)
      .filter((a) => matches(a.label))
      .map((a) => ({ ...a, group: 'Actions' as const }));

    const patientItem = (id: string, name: string, phone: string | undefined, group: PaletteItem['group'], icon: React.ReactNode): PaletteItem => ({
      key: `${group}-${id}`,
      group,
      label: name,
      hint: phone,
      icon,
      patientId: id,
      run: () => go(`/patients/${id}`),
    });

    const recent = isStaff && !debounced
      ? getRecentPatients()
          .filter((p) => matches(p.name) || (p.phone && matches(p.phone)))
          .map((p) => patientItem(p.id, p.name, p.phone, 'Recent patients', <History />))
      : [];

    const found = debounced.length >= 2
      ? patients.map((p: Patient) => patientItem(p.id, p.fullName, p.phoneNumber, 'Patients', <Person />))
      : [];

    return [...found, ...recent, ...actions];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term, debounced, patients, isDoctor, isStaff]);

  useEffect(() => setActive(0), [items.length]);

  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && items[active]) {
      e.preventDefault();
      // Ctrl/⌘+Enter on a patient starts a prescription for them
      const item = items[active];
      if ((e.metaKey || e.ctrlKey) && isDoctor && item.patientId) {
        go(`/prescriptions/create?patientId=${item.patientId}`);
      } else {
        item.run();
      }
    }
  };

  let lastGroup = '';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { alignSelf: 'flex-start', mt: 10 } }}>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 2, pb: 1 }}>
          <TextField
            fullWidth
            autoFocus
            placeholder={isStaff ? 'Search patients by name or phone, or type a command…' : 'Type a command…'}
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={onKeyDown}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: isFetching ? (
                <InputAdornment position="end">
                  <CircularProgress size={18} />
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Box>

        <List ref={listRef} dense sx={{ maxHeight: 420, overflow: 'auto', pt: 0 }}>
          {items.map((item, index) => {
            const header = item.group !== lastGroup ? item.group : null;
            lastGroup = item.group;
            return (
              <React.Fragment key={item.key}>
                {header && <ListSubheader sx={{ lineHeight: '32px' }}>{header}</ListSubheader>}
                <ListItemButton
                  data-index={index}
                  selected={index === active}
                  onMouseEnter={() => setActive(index)}
                  onClick={item.run}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} secondary={item.group !== 'Actions' ? item.hint : undefined} />
                  {item.group === 'Actions' && item.hint && <Chip size="small" label={item.hint} variant="outlined" />}
                </ListItemButton>
              </React.Fragment>
            );
          })}
          {items.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {debounced.length >= 2 && !isFetching ? 'No matches' : 'Keep typing…'}
              </Typography>
            </Box>
          )}
        </List>

        <Box sx={{ px: 2, py: 1, borderTop: 1, borderColor: 'divider', display: 'flex', gap: 2 }}>
          <Typography variant="caption" color="text.secondary">↑↓ navigate · Enter open</Typography>
          {isDoctor && <Typography variant="caption" color="text.secondary">Ctrl/⌘+Enter new prescription for patient</Typography>}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>Esc close</Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default GlobalSearch;
